import { TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { DECIMAL_KEY, formatDecimalTwoAsString, formatStringAsDecimalTwo } from "src/utils/decimal-two-utils";

export type TruxSimpleDecimalTwoFieldProps = {
  value: number,
  places?: number,
  allowNegative?: boolean,
  label?: string,
  disabled?: boolean,
  onChange?: (t: number) => boolean | void,
  textAlign?: "right" | "center" | "left"
}

const ALLOWED_KEYS = new Set<string>();
ALLOWED_KEYS.add("0");
ALLOWED_KEYS.add("1");
ALLOWED_KEYS.add("2");
ALLOWED_KEYS.add("3");
ALLOWED_KEYS.add("4");
ALLOWED_KEYS.add("5");
ALLOWED_KEYS.add("6");
ALLOWED_KEYS.add("7");
ALLOWED_KEYS.add("8");
ALLOWED_KEYS.add("9");
ALLOWED_KEYS.add("Backspace");
ALLOWED_KEYS.add("Delete");
ALLOWED_KEYS.add("ArrowLeft");
ALLOWED_KEYS.add("ArrowRight");
const NEGATIVE_KEY = "-";

export default function TruxSimpleDecimalTwoField<T>({ value, onChange, places = 2, allowNegative = false, label, disabled = false, textAlign }: TruxSimpleDecimalTwoFieldProps) {
  const currentValue = value;
  const [localValue, setLocalValue] = useState("0");

  useEffect(() => {
    if (formatStringAsDecimalTwo(`${localValue}`) !== currentValue) {
      setLocalValue(formatDecimalTwoAsString(currentValue as any))
    }
  }, [currentValue, setLocalValue, localValue]);

  const fieldOnKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = e.metaKey || ALLOWED_KEYS.has(e.key) || (places > 0 && e.key === DECIMAL_KEY) || (allowNegative && e.key === NEGATIVE_KEY);
    if (!allowed) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [allowNegative, places]);

  const fieldOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (disabled || !onChange) {
      return;
    }
    const val = e.target.value;

    const regex = new RegExp(`^-?\\d*(\\.\\d{0,${places}})?$`);

    if (val === "" || regex.test(val)) {
      setLocalValue(val as any);
      // we need to update our existing val to val
      const valNumber = formatStringAsDecimalTwo(`${val}`);

      onChange(valNumber);
    }
  }, [disabled, onChange, places]);

  return <TextField size="small" disabled={disabled} sx={{ display: "flex", width: "100%" }} label={label} inputProps={{ sx: { textAlign } }} value={localValue} onChange={fieldOnChange} onKeyDown={fieldOnKeyDown} />
}